import { isSafeButtonUrl, isSafeContentUrl } from './content-url';

export const RESERVED_PAGE_SLUGS = [
  'about',
  'admin',
  'api',
  'assets',
  'bulandi-2026',
  'bulandi-2026-admin',
  'events',
  'gallery',
  'leaderboard',
  'login',
  'member-registration',
  'members',
  'pages',
  'profile',
  'quiz',
  'scoreboard',
  'sponsors',
  'static',
] as const;

export type PageBlock =
  | { type: 'heading'; text: string }
  | { type: 'text'; markdown: string }
  | { type: 'image'; url: string; alt: string; caption: string | null; width: number | null; height: number | null }
  | { type: 'button'; label: string; url: string; style: 'primary' | 'secondary' }
  | { type: 'gallery'; albumSlug: string }
  | { type: 'divider' };

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,58})[a-z0-9]$/;

export function isReservedPageSlug(slug: string): boolean {
  return (RESERVED_PAGE_SLUGS as readonly string[]).includes(slug);
}

export function isPageSlug(slug: string): boolean {
  return SLUG_RE.test(slug) && !isReservedPageSlug(slug);
}

function text(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > max) return null;
  return trimmed;
}

function optionalText(value: unknown, max: number): string | null {
  if (value == null || value === '') return null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, max);
}

function dimension(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 20000) return null;
  return value;
}

function parseBlock(raw: unknown): PageBlock | null {
  if (!raw || typeof raw !== 'object') return null;
  const block = raw as Record<string, unknown>;
  switch (block.type) {
    case 'heading': {
      const value = text(block.text, 200);
      return value ? { type: 'heading', text: value } : null;
    }
    case 'text': {
      const markdown = text(block.markdown, 20000);
      return markdown ? { type: 'text', markdown } : null;
    }
    case 'image': {
      const url = text(block.url, 2000);
      if (!url || !isSafeContentUrl(url)) return null;
      const alt = typeof block.alt === 'string' ? block.alt.trim().slice(0, 300) : '';
      return {
        type: 'image',
        url,
        alt,
        caption: optionalText(block.caption, 500),
        width: dimension(block.width),
        height: dimension(block.height),
      };
    }
    case 'button': {
      const label = text(block.label, 80);
      const url = text(block.url, 2000);
      const style = block.style === 'secondary' ? 'secondary' : block.style === 'primary' ? 'primary' : null;
      if (!label || !url || !style || !isSafeButtonUrl(url)) return null;
      return { type: 'button', label, url, style };
    }
    case 'gallery': {
      const albumSlug = text(block.albumSlug, 60);
      if (!albumSlug || !SLUG_RE.test(albumSlug)) return null;
      return { type: 'gallery', albumSlug };
    }
    case 'divider':
      return { type: 'divider' };
    default:
      return null;
  }
}

export function parsePageBlocks(value: unknown): { ok: true; blocks: PageBlock[] } | { ok: false; error: string } {
  if (!Array.isArray(value)) return { ok: false, error: 'Blocks must be a list.' };
  if (value.length > 40) return { ok: false, error: 'A page can have at most 40 blocks.' };
  const blocks: PageBlock[] = [];
  for (const raw of value) {
    const block = parseBlock(raw);
    if (!block) return { ok: false, error: 'One of the blocks is incomplete or uses a link we cannot publish.' };
    blocks.push(block);
  }
  return { ok: true, blocks };
}

export function pageImageIdFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const match = url.trim().match(/\/api\/public\/pages\/images\/([a-zA-Z0-9_-]+)/);
  return match?.[1] ?? null;
}
